import { getRegionConfig } from 'config';
import {
  CONTENTFUL_ACCESS_TOKEN,
  CONTENTFUL_SPACE,
  getContentfulEnvironment,
} from 'config/contentful';
import {
  ContentfulClientApi,
  ContentfulCollection,
  Entry,
  EntryCollection,
  createClient,
} from 'contentful';
import getContentfulLocale from 'utils/i18n/get-contentful-locale';
import { getUrlLocalePrefix } from 'utils/i18n/locale-path-prefix';
import { IProductPageFields, IProductPagesFields, ISegmentationDataContainer } from '../global';
import { Locale, LOCALES } from 'config/constants/locales';
import { Region } from 'constants/shop';
import { loadExperienceVariants } from 'connectors/utils/load-experience-variants';
import { resolveReferences } from 'connectors/utils/resolve-references';

export const contentfulClient = createClient({
  space: CONTENTFUL_SPACE,
  accessToken: CONTENTFUL_ACCESS_TOKEN,
  removeUnresolved: true,
  environment: getContentfulEnvironment(),
});

export interface SegmentationDataResponse {
  fields: { segmentationData: ISegmentationDataContainer };
}

const requestCache = new Map<string, Promise<unknown>>();

const CACHE_TIME = 2000;

const isClient = typeof window !== 'undefined';

/**
 * Deduplicates and caches requests using a Map to store promises.
 * Prevents duplicate API calls by reusing cached promises within a specified time window.
 * Only applies caching on client-side, server-side requests execute directly.
 */
// TODO: all these utility repeats functionality of useSwr and make code less readable.
//  these pure function should not have any kind of protection or smart way to handle themself
//  the callers of these functions the ones that use them should take care of this.
const deduplicateRequest = async <T>(
  cacheKey: string,
  requestFn: () => Promise<T>,
  cacheTime = CACHE_TIME
): Promise<T> => {
  if (!isClient) return requestFn();

  if (!requestCache.has(cacheKey)) {
    const promise = requestFn()
      .catch((error) => {
        requestCache.delete(cacheKey);
        throw error;
      })
      .finally(() => {
        setTimeout(() => requestCache.delete(cacheKey), cacheTime);
      });

    requestCache.set(cacheKey, promise);
  }

  return requestCache.get(cacheKey) as Promise<T>;
};

export const fetchContentfulSegmentationData = async (
  handle
): Promise<ContentfulCollection<SegmentationDataResponse>> => {
  let result = await contentfulClient.getEntries<SegmentationDataResponse>({
    select: 'fields.segmentationData',
    content_type: 'productPage',
    'fields.handle': handle,
    include: 5,
  });

  if (result.items.length === 0) {
    result = await contentfulClient.getEntries<SegmentationDataResponse>({
      select: 'fields.segmentationData',
      content_type: 'productPages',
      'fields.handle': handle,
      include: 5,
    });
  }

  return result as unknown as ContentfulCollection<SegmentationDataResponse>;
};

interface FetchContenfulProductPage {
  handle: string;
  locale?: Locale;
  region: Region;
}

export const fetchContentfulProductPage = async ({
  handle,
  locale,
  region,
}: FetchContenfulProductPage): Promise<
  EntryWithHandle<IProductPageFields> | EntryWithHandle<IProductPagesFields> | null
> => {
  const contentful = await fetchContentfulByHandle({
    handle,
    locale,
    region,
    query: {
      content_type: 'productPage',
    },
  });

  if (contentful === null) {
    const contentfulLegacy = await fetchContentfulByHandle({ handle, locale, region });

    if (contentfulLegacy === null) {
      return null;
    }

    return contentfulLegacy;
  } else {
    return contentful;
  }
};

export const defaultQuery = {
  // when published is not defined in the model, we need to specifically override with 'undefined'
  'fields.published': true,
  include: 4,
};

export const fetchContentfulEntry = async <T>(
  id: Parameters<ContentfulClientApi['getEntry']>[0],
  locale?: string,
  query?: Parameters<ContentfulClientApi['getEntry']>[1]
): Promise<Entry<T>> => {
  const cacheKey = `entry-${id}-${locale}-${JSON.stringify(query)}`;

  return deduplicateRequest(cacheKey, async () => {
    const contentfulLocale = getContentfulLocale(locale);
    const entry = await contentfulClient.getEntry<T>(id, {
      ...defaultQuery,
      locale: contentfulLocale,
      ...query,
      'fields.published': undefined,
    });

    return loadExperienceVariants({
      node: entry,
      locale: contentfulLocale,
      // A variant replaces a page section, so it gets the depth a section has below the page.
      include: defaultQuery.include - 1,
    });
  });
};

export const fetchContentfulEntries = async <T>(
  query?: Parameters<ContentfulClientApi['getEntries']>[0]
): Promise<EntryCollection<T>> => {
  return await contentfulClient.getEntries<T>({
    ...defaultQuery,
    ...query,
  });
};

/** Keep the regional route handle separate from the German canonical fallback. */
export type EntryWithHandle<T> = Entry<T> & { handle: string; deHandle?: string };

/** Fetches explicit handles without Contentful's locale fallback. */
export const fetchLocalizedHandles = async (
  entryId: string
): Promise<Partial<Record<Locale, string>>> => {
  const { fields } = await contentfulClient.getEntry<{
    handle?: Partial<Record<Locale, string>>;
  }>(entryId, {
    locale: '*',
    include: 0,
    // Field-level `select` requires a content_type; this helper only knows the entry ID.
  });

  return fields.handle ?? {};
};

interface FetchContentfulByHandle {
  handle: string;
  locale?: Locale;
  region: Region;
  query?: Parameters<ContentfulClientApi['getEntries']>[0];
}

/**
 * Fetch entry from contentful API. if 'content_type' is not assigned, productPages is assigned.
 * For Type parameter, Fileds type is expected, such as IProductPagesFields.
 * @param handle : handle to identify entry
 * @param query : Object with search parameters.Check urls for more details
 * @returns
 * @see https://contentful.github.io/contentful.js/contentful/9.1.4/index.html
 */
export const fetchContentfulByHandle = async <T = IProductPagesFields>({
  handle,
  locale,
  region,
  query,
}: FetchContentfulByHandle): Promise<EntryWithHandle<T> | null> => {
  const cacheKey = `handle-${handle}-${locale}-${region}-${JSON.stringify(query)}`;
  const defaultRegionLocale = getRegionConfig(region).locale;
  const regionLocale = getUrlLocalePrefix(defaultRegionLocale);

  return deduplicateRequest(cacheKey, async () => {
    try {
      const entriesCollection = await contentfulClient.getEntries<T>({
        content_type: 'productPages',
        'fields.handle': handle, // handle is static for region (NL handle on NL region and so on)
        ...defaultQuery,
        ...query,
        locale: regionLocale, // lookup uses the normalized shop locale, independently of the content locale
        select: 'sys.id',
        include: 0,
      });

      if (entriesCollection.items.length === 0) {
        return null;
      }

      const firstEntry = entriesCollection.items[0];

      // Discover the entry via its regional route handle, then fetch its content in the UI locale.
      const [entry, deHandle] = await Promise.all([
        fetchContentfulEntry<T>(firstEntry.sys.id, locale),
        regionLocale === LOCALES.DE
          ? handle
          : contentfulClient
              .getEntry<{ handle?: string }>(firstEntry.sys.id, {
                content_type: query?.content_type ?? 'productPages',
                locale: LOCALES.DE,
                include: 0,
                select: 'fields.handle',
              })
              .then(({ fields }) => fields.handle),
      ]);

      return await resolveReferences({
        node: { ...entry, handle, ...(deHandle ? { deHandle } : {}) },
        region,
        locale: getContentfulLocale(locale),
      });
    } catch (error) {
      console.error(`Error while fetching handle : ${handle} `, error);
      return null;
    }
  });
};
